import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import FocusColorExtension from 'components/ProductPageV2/ProductDetails/ProductColors/SliderExtensions/FocusColorExtension';
import ColorBubble from 'components/Common/ColorBubble/ColorBubble';
import SlideColorBubbles from 'components/Common/SlideColorBubbles/SlideColorBubbles';
import Trans from 'components/Common/Translation/Trans';
import useProductColors from 'hooks/ProductPageV2/useProductColors';
import useCurrentCountry from 'hooks/useCurrentCountry';
import { getUri } from 'services/generic';
import { TABLET_PORTRAIT_MIN } from 'settings/device';
import { ProductColorsType } from 'types/ProductPageV2/Products';

import styles from './ProductColors.module.scss';

type Props = {
  colors: ProductColorsType[];
  color?: string;
  setColor: (color: string, e: SyntheticEvent) => void;
  editUrl?: string;
  classes?: { container?: string };
  maxItems: number;
  breakpoints?: Record<number, number>;
};

/**
 * Product page of website. Contains details product data.
 */
export default function ProductColors({
  colors,
  color,
  setColor,
  editUrl,
  classes,
  maxItems,
  breakpoints,
}: Props): JSX.Element {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<number | null>(
    null
  );

  const { isFr } = useCurrentCountry();
  const {
    selectedColor,
    selectedColorIndex,
    slideColors,
    useSlide,
  } = useProductColors(colors, color, editUrl);
  const [colorsList, setColorsList] = useState<ProductColorsType[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  const transAdditionalComponents = {
    Span: <span className={isFr ? styles.seeMoreSpan1 : styles.seeMoreSpan2} />,
  };

  const filteredColors = [...colors].filter(c => c !== selectedColor);
  const maxLength = breakpoints?.[currentBreakpoint ?? 0] || maxItems;

  const resizeHandler = useCallback(() => {
    if (!breakpoints) return;

    const screenWidth = window.innerWidth;
    const matchedBreakpoint = Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => b - a)
      .find(breakpoint => screenWidth >= breakpoint);

    setCurrentBreakpoint(matchedBreakpoint || null);
  }, [breakpoints]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler();

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [resizeHandler]);

  useEffect(() => {
    if (maxLength) {
      setColorsList(
        showAll || filteredColors.length === maxLength
          ? filteredColors
          : [...filteredColors].splice(0, maxLength - 1)
      );
    }
  }, [selectedColor, showAll, maxLength]);

  function renderColor(color: ProductColorsType, selected = false) {
    const { couleur, pastille, url } = color;
    const [baseUrl, hash] = url.split('#');

    return (
      <ColorBubble
        key={couleur}
        color={couleur}
        onClick={(e, color) => setColor(color, e)}
        classes={{
          container: classNames(
            selected && styles.selectedColor,
            styles.bubbleContainer
          ),
          image: styles.bubbleImage,
        }}
        image={pastille}
        url={getUri(
          hash ? `${baseUrl}${editUrl ?? ''}#${hash}` : `${url}${editUrl ?? ''}`
        )}
      >
        {selected && <span className={styles.label}>{couleur}</span>}
      </ColorBubble>
    );
  }

  return (
    <div
      className={classNames(styles.container, classes?.container, {
        [styles.withSlide]: useSlide,
      })}
    >
      <div className={styles.selectedColorWrapper}>
        {selectedColor && renderColor(selectedColor, true)}
      </div>

      {useSlide ? (
        <SlideColorBubbles
          bubbles={slideColors}
          classes={{
            container: styles.splide,
            arrow: styles.arrow,
            bubbles: {
              container: styles.bubbleContainer,
              image: styles.bubbleImage,
            },
          }}
          extensions={{
            focusColor: FocusColorExtension,
          }}
          onBubbleClick={({ color }, e) =>
            selectedColor?.couleur !== color && setColor(color, e)
          }
          options={{
            perPage: 4,
            breakpoints: {
              [TABLET_PORTRAIT_MIN]: {
                perPage: 6,
              },
            },
            focusColor: {
              focusIndex: selectedColorIndex,
            },
          }}
          selectedColor={selectedColor?.couleur}
        />
      ) : (
        <div
          className={classNames(styles.colorisList)}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${maxLength}, 1fr)`,
          }}
        >
          {colorsList.map(c => renderColor(c))}
          {maxLength && filteredColors.length > maxLength && (
            <div className={styles.buttonContainer}>
              <button
                className={styles.showMoreBtn}
                onClick={() => {
                  setShowAll(!showAll);
                }}
              >
                <Trans
                  i18nKey={showAll ? 'LABEL_SEE_LESS' : 'LABEL_SEE_MORE'}
                  additionalComponents={transAdditionalComponents}
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
